import React, {
  Fragment,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { equals, length, mergeRight, nth, omit, pluck } from "ramda";
import {
  useCollection,
  useCollectionApprovedContent,
  useCollectionContentTypeSummary,
  useRemoveContentFromCollection,
} from "../../collections/collectionServices";
import { faFolderPlus } from "@fortawesome/pro-regular-svg-icons";
import { useSelected } from "../OrganizeProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { notBlank } from "../../../util/ext";
import { useSearchQuery } from "../../common/use-searchQuery";
import { GroupBrowserContext } from "../OrganizeView";
import { formatSort } from "../../../util/stringFormat";
import { parseTitleQuery, parseIdQuery } from "../../../util/fetchUtils";

import ContentGroupFilesBrowser from "./ContentGroupFilesBrowser";
import ContentGroupHeader from "./ContentGroupHeader";
import ContentGroupHeaderActions from "./ContentGroupHeaderActions";
import ContentGroupsBreadcrumbs from "./ContentGroupsBreadcrumbs";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import OrganizeContentGroupsBrowser from "./OrganizeContentGroupsBrowser";
import RealTimeSearchBar from "../../common/RealTimeSearchBar";

const initialContentState = {
  appliedQuery: "",
  page: 0,
  limit: 10,
  orderBy: null,
  orderDirection: "asc",
  "ct.mediaId": "",
  "ct.title": "",
  source: {
    projectKey: "",
    projectName: "",
  },
};

const initialDefaultSort = {
  orderBy: "title",
  orderDirection: "asc",
};

const getInitialSearchState = initialState =>
  mergeRight(initialContentState, initialState);

const OrganizeCollectionBrowser = props => {
  const { breadcrumbs, id, view, SnackAlertHandler } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const groupBrowser = useContext(GroupBrowserContext);
  const { showAlert, SnackAlert } = SnackAlertHandler;

  const { selectedContent, removeAllSelectedContent } = useSelected();

  //-- Manage Collection
  const collectionResults = useCollection(id);
  const [collection, setCollection] = useState([]);
  const [collectionParent, setCollectionParent] = useState({});
  useEffect(() => {
    if (collectionResults.isSuccess) {
      setCollection(collectionResults.data);
      // Existence of parent module determines which header action render
      // TODO: Handle when breadcrumbs are absent and collection has more than one parent module
      setCollectionParent(
        nth(-2, breadcrumbs) || collectionResults.data.parentModules[0] || {},
      );
    }
  }, [breadcrumbs, collectionResults.isSuccess, collectionResults.data]);

  //-- Manage Collection Content
  const initialState = getInitialSearchState(
    notBlank(location.state) ? location.state.searchState : {},
  );
  const contentQuery = useSearchQuery(initialState);
  const [contentState, setContentState] = useState({
    ...initialState,
    source: contentQuery.state.source,
    ofset: contentQuery.state.limit * contentQuery.state.page,
    sort: formatSort(initialDefaultSort),
  });

  useEffect(() => {
    const { limit, page, appliedQuery } = contentQuery.state;

    let defaultSort;
    if (
      collection.defaultContentSort &&
      collection.defaultContentSort !== "collectionDisplayOrder"
    ) {
      defaultSort = collection.defaultContentSort;
    } else {
      defaultSort = formatSort(initialDefaultSort);
    }
    const sort =
      contentQuery.state.orderBy === null
        ? defaultSort
        : formatSort(contentQuery.state);

    const queryState = omit(
      ["orderBy", "orderDirection", "page"],
      contentQuery.state,
    );

    setContentState({
      ...queryState,
      sort,
      offset: limit * page,
      "ct.title": parseTitleQuery(appliedQuery),
      "ct.mediaId": parseIdQuery(appliedQuery),
    });
  }, [
    collection.defaultContentSort,
    contentQuery.state,
    initialState.orderBy,
    initialState.orderDirection,
  ]);

  const contentResults = useCollectionApprovedContent(id, {
    ...contentState,
    source: contentState.source.projectKey,
  });
  const contentTotals = useCollectionContentTypeSummary(id, {
    ...contentState,
    source: contentState.source.projectKey,
  });
  const [content, setContent] = useState([]);
  useEffect(() => {
    if (contentResults.isSuccess) {
      setContent(contentResults.data);
    }
  }, [contentResults.isSuccess, contentResults.data]);

  const handleEditClick = () => {
    const { pathname, search } = location;
    const fromPath = notBlank(search) ? pathname + search : pathname;
    navigate(`/creator/collections/${id}`, {
      state: {
        from: fromPath,
      },
    });
  };

  //-- Handle Removing Content from Collection
  const removeContentFromCollection = useRemoveContentFromCollection();
  const handleDeleteItems = useCallback(async () => {
    try {
      await removeContentFromCollection.mutateAsync({
        collectionId: id,
        contentIds: pluck("id", selectedContent),
      });

      const itemText = equals(length(selectedContent), 1) ? "Item" : "Items";

      showAlert("success", [
        `${length(selectedContent)} ${itemText} Successfully Removed`,
      ]);
    } catch (error) {
      showAlert("error", [error.message]);
    } finally {
      removeAllSelectedContent();
    }
  }, [
    removeContentFromCollection,
    id,
    removeAllSelectedContent,
    selectedContent,
    showAlert,
  ]);

  //-- Loading State
  const [contentLoading, setContentLoading] = useState(false);
  useEffect(() => {
    setContentLoading(
      contentResults.isLoading ||
        removeContentFromCollection.isPending ||
        groupBrowser.loading,
    );
  }, [
    contentResults.isLoading,
    removeContentFromCollection.isPending,
    groupBrowser.loading,
  ]);

  return (
    <Fragment>
      <SnackAlert />

      <OrganizeContentGroupsBrowser
        view={view}
        header={
          <ContentGroupHeader
            loading={collectionResults.isLoading}
            view={view}
            breadcrumbs={
              <ContentGroupsBreadcrumbs
                media={collectionResults}
                breadcrumbs={breadcrumbs}
              />
            }
            actions={
              collectionResults.isLoading ? null : (
                <ContentGroupHeaderActions
                  group={collection}
                  groupParent={collectionParent}
                  handleEditClick={handleEditClick}
                  action="move"
                  moveActionTitle="Move"
                  moveActionIcon={faFolderPlus}
                />
              )
            }
          />
        }
        filesPanel={
          <ContentGroupFilesBrowser
            searchState={contentState}
            sourceFilter={contentQuery.state.source}
            searchBar={
              <RealTimeSearchBar
                searchBarEnd
                query={contentQuery.state.appliedQuery}
                onQueryChange={query => contentQuery.handleUpdateQuery(query)}
              />
            }
            filterMenu={
              <ContentSourcesAutocomplete
                setSource={contentQuery.handleUpdateSource}
                source={contentQuery.state.source}
              />
            }
            loading={contentLoading}
            setLoading={setContentLoading}
            view={view}
            expanded={{ outer: false, inner: false }}
            parent={collection}
            content={content}
            total={contentTotals.isLoading ? "-" : contentTotals.data._ALL}
            handleDeleteItems={handleDeleteItems}
            handlePageChange={(event, newPage) =>
              contentQuery.handleUpdatePage(newPage)
            }
            handleSortChange={contentQuery.handleUpdateSort}
            onRowsPerPageChange={contentQuery.handleUpdateLimit}
            rowsPerPage={contentQuery.state.limit}
            page={contentQuery.state.page}
            orderBy={contentQuery.state.orderBy}
            order={contentQuery.state.orderDirection}
            selectedContent={selectedContent}
            SnackAlertHandler={SnackAlertHandler}
          />
        }
      />
    </Fragment>
  );
};

export default OrganizeCollectionBrowser;
